import { Point } from "geojson";
import JSON5 from "json5";
import { format, parseISO } from "date-fns";

import { Api } from "./Api";
import { parseOrganizationData } from "./Organization";

export const getBagObjects = async (
  leadSearchId: string,
): Promise<IBagObject[]> => {
  /**
   * Retrieve BAG objects for a lead search.
   */
  return Api.get(`lead-searches/${leadSearchId}/bag-objects/`)
    .then((result: IApiSuccess) => {
      const bagObjectsData = result.data as IAxiosResponseData[];
      return bagObjectsData.map((bagObjectData) =>
        parseBagObjectData(bagObjectData),
      );
    })
    .catch((error: IApiError) => Promise.reject(error));
};

export const updateBagObjectAppData = async (
  bagObjectId: string,
  bagObjectAppData: IBagObjectAppData,
): Promise<void> => {
  /**
   * Update a BAG object's app data.
   */
  const path = `/bag-objects/${bagObjectId}/app-data/`;
  const formData = {
    notes: bagObjectAppData.notes,
    clear_height: bagObjectAppData.clearHeight,
    office_area: bagObjectAppData.officeArea,
    max_floor_load: bagObjectAppData.maxFloorLoad,
  };
  await Api.post(path, formData);
};

export const updateLease = async (
  bagObjectId: string,
  lease: ILease,
): Promise<void> => {
  /**
   * Create/update a lease.
   */
  const path = `/bag-objects/${bagObjectId}/lease/`;
  const formData = {
    owner_name: lease.ownerName,
    owner_kvk: lease.ownerKvk,
    tenant_name: lease.tenantName,
    tenant_kvk: lease.tenantKvk,
    broker_name: lease.brokerName,
    broker_kvk: lease.brokerKvk,
    incentives: lease.incentives,
    notes: lease.notes,
    price_per_m2_per_year: lease.pricePerM2PerYear,
    start_date: lease.startDate ? format(lease.startDate, "yyyy-MM-dd") : null,
    end_date: lease.endDate ? format(lease.endDate, "yyyy-MM-dd") : null,
  };
  await Api.post(path, formData);
};

export const parseBagObjectAppDataData = (
  bagObjectAppDataData: IAxiosResponseData,
): IBagObjectAppData => {
  /**
   * Parse BAG object app data from the API response, to an IBagObjectAppData object.
   */
  return {
    notes: bagObjectAppDataData.notes,
    clearHeight: bagObjectAppDataData.clear_height,
    officeArea: bagObjectAppDataData.office_area,
    maxFloorLoad: bagObjectAppDataData.max_floor_load,
  } as IBagObjectAppData;
};

export const parseLeaseData = (leaseData: IAxiosResponseData): ILease => {
  /**
   * Parse lease from the API response, to an ILease object.
   */
  return {
    ownerName: leaseData.owner_name,
    ownerKvk: leaseData.owner_kvk,
    tenantName: leaseData.tenant_name,
    tenantKvk: leaseData.tenant_kvk,
    brokerName: leaseData.broker_name,
    brokerKvk: leaseData.broker_kvk,
    incentives: leaseData.incentives,
    notes: leaseData.notes,
    pricePerM2PerYear: leaseData.price_per_m2_per_year,
    startDate: leaseData.start_date ? parseISO(leaseData.start_date) : null,
    endDate: leaseData.end_date ? parseISO(leaseData.end_date) : null,
  } as ILease;
};

export const parseBagObjectData = (
  bagObjectData: IAxiosResponseData,
): IBagObject => {
  /**
   * Parse BAG object data from the API response, to an IBagObject object.
   */
  const centroid: Point = bagObjectData.geom_wgs84;
  const organizations: IOrganization[] = bagObjectData.organizations.map(
    (organizationData: IAxiosResponseData[]) =>
      parseOrganizationData(organizationData),
  );
  return {
    id: bagObjectData.nummeraanduiding,
    objectId: bagObjectData.object_id,
    addressFormatted: bagObjectData.address_formatted,
    floorArea: bagObjectData.oppervlakteverblijfsobject,
    yearOfConstruction: bagObjectData.pandbouwjaar,
    functions: JSON5.parse(bagObjectData.verblijfsobjectgebruiksdoel),
    status: bagObjectData.verblijfsobjectstatus,
    buildingId: bagObjectData.pandid,
    buildingStatus: bagObjectData.pandstatus,
    centroid: centroid,
    organizations: organizations,
    organizationsCount: organizations.length,
    mainOrganizationName: bagObjectData.main_organization_name,
    appData: bagObjectData.app_data
      ? parseBagObjectAppDataData(bagObjectData.app_data)
      : null,
    lease: bagObjectData.lease ? parseLeaseData(bagObjectData.lease) : null,
    perceelOppervlakte: bagObjectData.perceel_oppervlakte,
    kadastraleAanduiding: bagObjectData.kadastrale_aanduiding,
  } as IBagObject;
};

export const getBagObjectPerceel = async (
  bagObjectId: string,
): Promise<IPerceelDetail> => {
  /**
   * Get a BAG object's parcel (with polygon). 404 means: no parcel.
   */
  const path = `/bag-objects/${bagObjectId}/perceel/`;
  return Api.get(path)
    .then((result: IApiSuccess) => {
      const perceelData = result.data as IAxiosResponseData;
      return {
        kadastraleAanduiding: perceelData.kadastrale_aanduiding,
        kadastraleGrootte: perceelData.kadastrale_grootte,
        polygon: perceelData.polygon,
      } as IPerceelDetail;
    })
    .catch((error: IApiError) => Promise.reject(error));
};
